import type { Tool } from '../../links/growthPlatformsLinks';
import { appLink, type PluginEntry } from '../../remotes/registry';
import type { LocalUsageMap } from '../../usage/track';
import type { AppCategoryId } from './appStoreVocabulary';

/** One Quick Launch entry: an app or a tool the user opened, and where its link goes. */
export interface QuickLaunchItem {
  target: `app:${string}` | `tool:${string}`;
  kind: 'app' | 'tool';
  id: string;
  name: string;
  href: string;
  external: boolean;
  category?: AppCategoryId | undefined;
  count: number;
  seq: number;
}

/**
 * The user's `limit` (default 3) most-opened apps and tools from this browser's
 * history, most opens first, ties broken by the most recent open (`seq`). History
 * for an app or tool that is no longer listed is skipped.
 *
 * Pure, like launcherModel, but its own module: it needs appLink from registry.ts,
 * which imports the SDK barrel.
 */
export function topQuickLaunchItems(
  apps: readonly PluginEntry[],
  tools: readonly Tool[],
  usage: Readonly<LocalUsageMap>,
  limit = 3,
): QuickLaunchItem[] {
  const appById = new Map(apps.map((a) => [a.appId, a]));
  const toolById = new Map(tools.map((t) => [t.id, t]));
  const ranked: QuickLaunchItem[] = [];

  for (const [target, { count, seq }] of Object.entries(usage)) {
    if (target.startsWith('app:')) {
      const app = appById.get(target.slice(4));
      if (!app) continue;
      ranked.push({
        target: `app:${app.appId}`,
        kind: 'app',
        id: app.appId,
        name: app.displayName,
        ...appLink(app),
        category: app.category,
        count,
        seq,
      });
    } else if (target.startsWith('tool:')) {
      const tool = toolById.get(target.slice(5));
      if (!tool) continue;
      ranked.push({
        target: `tool:${tool.id}`,
        kind: 'tool',
        id: tool.id,
        name: tool.name,
        href: tool.href,
        external: true,
        category: tool.category,
        count,
        seq,
      });
    }
  }

  ranked.sort((a, b) => b.count - a.count || b.seq - a.seq);
  return ranked.slice(0, limit);
}
